import React, { useEffect, useState } from 'react';
import { FaTrash, FaArrowLeft, FaArrowRight } from 'react-icons/fa';
import { useCart } from './CartContext'; 
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ShoppingCart, Tag, Award, Star, CheckCircle } from 'lucide-react';

// --- INTERFACES ---
interface Product {
    id: number;
    name: string;
    imageUrl: string;
    price: number;
    rating?: number;
    discount?: string;
    sellerId?: number;
    companyName?: string;
}

const CartPage: React.FC = () => {
    const { cart, cartItemCount, removeFromCart, addToCart } = useCart() as any; 
    const navigate = useNavigate();
    
    // States
    const [similarProducts, setSimilarProducts] = useState<Product[]>([]);
    const [couponCode, setCouponCode] = useState('');
    const [discountAmount, setDiscountAmount] = useState(0);
    const [couponMessage, setCouponMessage] = useState<{text: string, type: 'success' | 'error'} | null>(null);

    // --- REAL CALCULATION ---
    const subtotal = cart.reduce((acc: number, item: any) => acc + (Number(item.price) || 0) * item.quantity, 0);
    const tax = subtotal * 0.18; 
    const shipping = subtotal > 5000 ? 0.00 : 150.00; 
    const total = subtotal + tax + shipping - discountAmount;

    // --- COUPON LOGIC ---
    const handleApplyCoupon = () => {
        if (couponCode.toUpperCase() === 'TRADY10') {
            const disc = subtotal * 0.10; 
            setDiscountAmount(disc);
            setCouponMessage({ text: 'Coupon Applied! 10% Off', type: 'success' });
        } else if (couponCode.toUpperCase() === 'WELCOME500') {
            setDiscountAmount(500); 
            setCouponMessage({ text: 'Welcome Bonus Applied! ₹500 Off', type: 'success' });
        } else {
            setDiscountAmount(0);
            setCouponMessage({ text: 'Invalid Coupon Code', type: 'error' });
        }
    };

    const isCartEmpty = cartItemCount === 0;

    // --- FETCH SIMILAR PRODUCTS ---
    useEffect(() => {
        const fetchSimilar = async () => {
            try {
                const response = await axios.get('http://localhost:2026/api/bestsellers');
                setSimilarProducts(response.data.slice(0, 5)); 
            } catch (error) {
                console.error("Error fetching similar products:", error);
            }
        };
        fetchSimilar();
    }, []);

    const handleAddRecommendation = (e: React.MouseEvent, product: Product) => {
        e.preventDefault();
        e.stopPropagation();
        addToCart({
            id: product.id,
            name: product.name,
            price: product.price,
            image: product.imageUrl,
            quantity: 1
        } as any);
    };

    // --- EMPTY CART UI ---
    const renderEmptyCart = () => (
        <div className="flex flex-col items-center justify-center py-16 px-4">
            <img
                src="https://www.pngall.com/wp-content/uploads/2016/07/Online-Shopping-PNG-Picture.png"
                alt="Empty Cart"
                className="w-64 md:w-80 h-auto object-contain opacity-80 mb-6"
            />
            <h2 className="text-2xl md:text-3xl font-extrabold text-gray-800 mb-2 text-center">Your Cart is Empty!</h2>
            <p className="text-sm md:text-base text-gray-500 text-center mb-8">
                Looks like you haven't added anything yet.
            </p>
            <button 
                onClick={() => navigate('/')} 
                className="inline-flex items-center bg-teal-600 text-white font-bold tracking-wide text-sm md:text-lg py-3 px-8 rounded-full hover:bg-teal-700 transition duration-300 shadow-lg transform hover:-translate-y-1"
            >
                <FaArrowLeft className="mr-2" /> Start Shopping
            </button>
        </div>
    );

    return (
        <div className="min-h-screen bg-gray-50 font-sans pb-20">
            <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-6 md:py-10">
                
                {isCartEmpty ? (
                    renderEmptyCart()
                ) : (
                    // --- FILLED CART LAYOUT ---
                    // Responsive Grid: 1 col on Mobile, 3 cols on Large Screens
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
                        
                        {/* LEFT COLUMN: Cart Items List */}
                        <div className="lg:col-span-2 space-y-4">
                            <h1 className="text-xl md:text-2xl font-bold text-gray-800 mb-4">Shopping Cart <span className='text-teal-600'></span></h1>
                            
                            <div className="bg-white rounded-xl shadow-sm border border-gray-200 divide-y divide-gray-100">
                                {cart.map((item: any) => (
                                    <div key={item.id} className="flex flex-col sm:flex-row items-center p-4 md:p-6 gap-4 sm:gap-6 group">
                                        
                                        {/* Product Image */}
                                        <div className="w-24 h-24 flex-shrink-0 border border-gray-100 rounded-lg overflow-hidden bg-gray-50 p-2 relative">
                                            <img 
                                                src={item.image || "https://via.placeholder.com/150?text=No+Img"} 
                                                alt={item.name} 
                                                className="w-full h-full object-contain mix-blend-multiply group-hover:scale-110 transition-transform"
                                            />
                                        </div>
                                        
                                        {/* Product Details */}
                                        <div className="flex-grow text-center sm:text-left w-full">
                                            <h4 
                                                className="font-bold text-gray-800 text-base md:text-lg line-clamp-2 hover:text-teal-600 cursor-pointer transition-colors"
                                                onClick={() => navigate(`/product/${item.id}`)}
                                            >
                                                {item.name}
                                            </h4>
                                            <p className="text-sm text-gray-500 mt-1">Quantity: {item.quantity}</p>
                                        </div>

                                        {/* Price & Remove */}
                                        <div className="flex flex-col items-center sm:items-end w-full sm:w-auto">
                                            <p className="text-lg md:text-xl font-bold text-gray-900 mb-2">
                                                ₹ {(Number(item.price) * item.quantity).toFixed(2)}
                                            </p>
                                            
                                            <button 
                                                onClick={() => removeFromCart(item.id)} 
                                                className="text-xs md:text-sm text-red-500 hover:text-red-700 hover:bg-red-50 px-3 py-1.5 rounded-full transition-all flex items-center font-medium border border-transparent hover:border-red-100"
                                            >
                                                <FaTrash className='mr-1.5 w-3 h-3' /> Remove
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            
                            <button onClick={() => navigate('/')} className="inline-flex items-center text-teal-600 hover:text-teal-800 font-semibold transition duration-200 mt-4 text-sm md:text-base">
                                <FaArrowLeft className="mr-2" /> Continue Shopping
                            </button>
                        </div>

                        {/* RIGHT COLUMN: Order Summary & Coupon */}
                        {/* Mobile: Spacing top, Desktop: Sticky top */}
                        <div className="mt-6 lg:mt-0 lg:sticky lg:top-24 h-fit space-y-6 pt-14">
                            

                            {/* ORDER SUMMARY */}
                            <div className="bg-white p-6 rounded-xl shadow-md border border-teal-50">
                                <h3 className="text-lg font-bold mb-4 text-gray-800 border-b border-gray-100 pb-3">Order Summary</h3>
                                <div className="space-y-3 text-gray-600 text-sm">
                                    <div className="flex justify-between">
                                        <span>Subtotal</span>
                                        <span className="font-medium text-gray-900">₹ {subtotal.toFixed(2)}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span>Shipping</span>
                                        <span className={shipping === 0 ? "text-green-600 font-bold" : "font-medium text-gray-900"}>
                                            {shipping === 0 ? "FREE" : `₹ ${shipping.toFixed(2)}`}
                                        </span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span>Tax (18% GST)</span>
                                        <span className="font-medium text-gray-900">₹ {tax.toFixed(2)}</span>
                                    </div>
                                    
                                    {/* Discount Row */}
                                    {discountAmount > 0 && (
                                        <div className="flex justify-between text-green-600 font-bold bg-green-50 p-2 rounded">
                                            <span>Discount Applied</span>
                                            <span>- ₹ {discountAmount.toFixed(2)}</span>
                                        </div>
                                    )}
                                    
                                    <div className="border-t border-dashed border-gray-300 pt-4 mt-2">
                                        <div className="flex justify-between text-xl font-bold text-gray-900">
                                            <span>Total</span>
                                            <span>₹ {total.toFixed(2)}</span>
                                        </div>
                                        <p className="text-[10px] text-gray-400 mt-1 text-right">(Inclusive of all taxes)</p>
                                    </div>
                                </div>
                                <button 
                                    onClick={() => navigate('/checkout')} 
                                    className="w-full bg-teal-600 text-white font-bold py-3.5 mt-6 rounded-lg hover:bg-teal-700 transition duration-200 shadow-lg transform active:scale-95 flex justify-center items-center gap-2"
                                >
                                    PROCEED TO CHECKOUT <FaArrowRight className="w-3 h-3"/>
                                </button>
                                <div className="flex justify-center gap-2 mt-4 opacity-50">
                                    <div className="w-8 h-5 bg-gray-200 rounded"></div>
                                    <div className="w-8 h-5 bg-gray-200 rounded"></div>
                                    <div className="w-8 h-5 bg-gray-200 rounded"></div>
                                    <div className="w-8 h-5 bg-gray-200 rounded"></div>
                                </div>
                            </div>

                             {/* COUPON SECTION */}
                            <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-200">
                                <h3 className="text-xs font-bold text-gray-500 uppercase mb-3 flex items-center gap-2">
                                    <Tag className="w-4 h-4" /> Have a Coupon?
                                </h3>
                                <div className="flex gap-2">
                                    <input 
                                        type="text" 
                                        placeholder="Enter code" 
                                        className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-teal-500 uppercase font-medium"
                                        value={couponCode}
                                        onChange={(e) => setCouponCode(e.target.value)}
                                    />
                                    <button 
                                        onClick={handleApplyCoupon}
                                        className="bg-gray-900 text-white px-4 py-2 rounded-lg text-xs font-bold hover:bg-black transition-colors tracking-wide"
                                    >
                                        APPLY
                                    </button>
                                </div>
                                {couponMessage && (
                                    <p className={`text-xs mt-2 font-bold ${couponMessage.type === 'success' ? 'text-green-600' : 'text-red-500'}`}>
                                        {couponMessage.text}
                                    </p>
                                )}
                            </div>
                        </div>

                    </div>
                )}

                {/* --- BOTTOM SECTION: SIMILAR PRODUCTS --- */}
                {similarProducts.length > 0 && (
                    <div className="mt-16 md:mt-24 pt-10 border-t border-gray-200">
                        <div className="flex flex-col sm:flex-row justify-between items-end mb-8 gap-4 border-b border-gray-100 pb-4">
                            <div>
                                <h2 className="text-xl md:text-2xl font-bold text-gray-900">You might also like</h2>
                                <p className="text-sm text-gray-500 mt-1">Frequently bought together with items in your cart</p>
                            </div>
                            <button 
                                onClick={() => navigate('/all-categories')}
                                className="flex items-center gap-2 text-sm font-bold text-teal-600 hover:text-white hover:bg-teal-600 border border-teal-600 px-5 py-2 rounded-full transition-all duration-300"
                            >
                                View All Categories <FaArrowRight className="w-3 h-3" />
                            </button>
                        </div>
                        
                        {/* Responsive Grid for Products */}
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-5">
                            {similarProducts.map((prod, index) => (
                                <article 
                                    key={prod.id} 
                                    className="group relative bg-white border border-gray-200 rounded-xl overflow-hidden hover:shadow-xl transition-all duration-300 flex flex-col h-full hover:-translate-y-1 cursor-pointer"
                                    onClick={() => navigate(`/product/${prod.id}`)}
                                >
                                    {/* Image Area */}
                                    <div className="relative w-full aspect-[4/3] bg-gray-50 flex items-center justify-center p-4 overflow-hidden">
                                        <div className="absolute top-0 left-0 bg-teal-600 text-white text-[10px] font-bold px-2 py-1 rounded-br-lg z-20 shadow-sm flex items-center gap-1">
                                            <Award className="w-3 h-3" /> #{index + 1}
                                        </div>
                                        <img 
                                            src={prod.imageUrl} 
                                            alt={prod.name} 
                                            className="w-full h-full object-contain transition-transform duration-500 group-hover:scale-110 mix-blend-multiply"
                                        />
                                        
                                        {/* Quick Add Button on Hover */}
                                        <div className="absolute inset-x-0 bottom-2 px-2 flex items-center gap-2 translate-y-full opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-300 z-20">
                                            <button
                                                onClick={(e) => handleAddRecommendation(e, prod)}
                                                className="w-full bg-teal-600 text-white text-xs font-bold py-2 rounded shadow-lg text-center hover:bg-teal-700 transition-colors flex justify-center items-center gap-1"
                                            >
                                                <ShoppingCart className="w-3 h-3" /> Add
                                            </button>
                                        </div>
                                    </div>

                                    {/* Content */}
                                    <div className="p-3 flex flex-col flex-grow border-t border-gray-50">
                                        <h3 className="text-xs md:text-sm font-semibold text-gray-900 line-clamp-2 leading-snug mb-2 group-hover:text-teal-700 transition-colors h-8">
                                            {prod.name}
                                        </h3>

                                        <div className="mt-auto flex items-center justify-between">
                                            <span className="text-sm md:text-base font-bold text-gray-900">
                                                ₹ {new Intl.NumberFormat('en-IN').format(prod.price)}
                                            </span>
                                            <div className="flex items-center gap-1 bg-amber-50 px-1.5 py-0.5 rounded text-[10px] font-bold text-amber-700">
                                                4.5 <Star className="w-2 h-2 fill-amber-500 text-amber-500" />
                                            </div>
                                        </div>
                                    </div>
                                </article>
                            ))}
                        </div>
                    </div>
                )}

            </div>
        </div>
    );
};

export default CartPage;